import React from 'react';
import { Box, Stack, Typography, Fade } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { getNeonColor } from '../theme';

// ============================================
// NEON TABLE CONTAINER
// ============================================

interface NeonTableContainerProps {
    children: React.ReactNode;
    sx?: object;
}

export const NeonTableContainer: React.FC<NeonTableContainerProps> = ({ children, sx }) => (
    <Stack
        direction="column"
        spacing={0.5}
        sx={{
            width: '100%',
            ...sx
        }}
    >
        {children}
    </Stack>
);

// ============================================
// NEON TABLE HEADER ROW
// ============================================

interface NeonTableHeaderProps {
    children: React.ReactNode;
    sticky?: boolean;
}

export const NeonTableHeader: React.FC<NeonTableHeaderProps> = ({ children, sticky = true }) => {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                padding: '10px 16px',
                borderRadius: '6px',
                backgroundColor: 'rgba(255,255,255,0.02)',
                borderBottom: `1px solid rgba(255,255,255,0.05)`,
                marginBottom: '4px',
                ...(sticky && {
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    backdropFilter: 'blur(10px)',
                }),
            }}
        >
            {children}
        </Box>
    );
};

// ============================================
// NEON TABLE HEADER CELL
// ============================================

interface NeonTableHeaderCellProps {
    children?: React.ReactNode;
    width?: string;
    minWidth?: string;
    align?: 'left' | 'center' | 'right';
    flex?: number;
}

export const NeonTableHeaderCell: React.FC<NeonTableHeaderCellProps> = ({ 
    children, 
    width, 
    minWidth,
    align = 'center',
    flex 
}) => (
    <Typography
        variant="caption"
        sx={{
            width: flex ? undefined : width,
            minWidth: minWidth,
            flex: flex,
            textAlign: align,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.8px',
            color: 'rgba(255,255,255,0.4)',
            fontSize: '0.6rem',
            flexShrink: flex ? 1 : 0,
        }}
    >
        {children}
    </Typography>
);

// ============================================
// NEON TABLE ROW
// ============================================

interface NeonTableRowProps {
    children: React.ReactNode;
    onClick?: () => void;
    isSelected?: boolean;
    variant?: 'default' | 'success' | 'error';
    animationDelay?: number;
    sx?: object;
}

export const NeonTableRow: React.FC<NeonTableRowProps> = ({ 
    children, 
    onClick, 
    isSelected = false,
    variant = 'default',
    animationDelay = 0,
    sx 
}) => {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    
    const variantStyles = {
        default: {
            background: 'rgba(255,255,255,0.02)',
            hoverBackground: 'rgba(255,255,255,0.05)',
        },
        success: {
            background: 'linear-gradient(90deg, rgba(46, 204, 113, 0.06) 0%, rgba(46, 204, 113, 0.02) 100%)',
            hoverBackground: 'linear-gradient(90deg, rgba(46, 204, 113, 0.12) 0%, rgba(46, 204, 113, 0.04) 100%)',
        },
        error: {
            background: 'linear-gradient(90deg, rgba(231, 76, 60, 0.06) 0%, rgba(231, 76, 60, 0.02) 100%)',
            hoverBackground: 'linear-gradient(90deg, rgba(231, 76, 60, 0.12) 0%, rgba(231, 76, 60, 0.04) 100%)',
        }
    };

    const styles = variantStyles[variant];

    return (
        <Fade in timeout={200 + animationDelay * 30}>
            <Box
                onClick={onClick}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    minHeight: '52px',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    cursor: onClick ? 'pointer' : 'default',
                    position: 'relative',
                    background: styles.background,
                    border: isSelected 
                        ? `1px solid ${neonColor}40`
                        : '1px solid transparent',
                    boxShadow: isSelected 
                        ? `inset 0 0 20px ${neonColor}10`
                        : 'none',
                    transition: 'all 0.15s ease-out',
                    '&:hover': onClick ? {
                        background: styles.hoverBackground,
                        border: `1px solid rgba(255,255,255,0.08)`,
                    } : {},
                    '&::before': isSelected ? {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '20%',
                        height: '60%',
                        width: '2px',
                        borderRadius: '0 2px 2px 0',
                        background: neonColor,
                        boxShadow: `0 0 8px ${neonColor}`,
                    } : {},
                    ...sx
                }}
            >
                {children}
            </Box>
        </Fade>
    );
};

// ============================================
// NEON TABLE CELL
// ============================================

interface NeonTableCellProps {
    children: React.ReactNode;
    width?: string;
    minWidth?: string;
    align?: 'left' | 'center' | 'right';
    flex?: number;
    sx?: object;
}

export const NeonTableCell: React.FC<NeonTableCellProps> = ({ 
    children, 
    width, 
    minWidth,
    align = 'center',
    flex,
    sx 
}) => (
    <Box
        sx={{
            width: flex ? undefined : width,
            minWidth: minWidth,
            flex: flex,
            display: 'flex',
            alignItems: 'center',
            justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center',
            flexShrink: flex ? 1 : 0,
            ...sx
        }}
    >
        {children}
    </Box>
);

// ============================================
// NEON SCORE BADGE
// ============================================

interface NeonScoreBadgeProps {
    score: number | string;
    variant?: 'win' | 'loss' | 'neutral';
    size?: 'small' | 'medium';
}

export const NeonScoreBadge: React.FC<NeonScoreBadgeProps> = ({ 
    score, 
    variant = 'neutral',
    size = 'medium' 
}) => {
    const colors = {
        win: {
            bg: 'rgba(46, 204, 113, 0.15)',
            border: 'rgba(46, 204, 113, 0.3)',
            text: '#2ecc71',
            glow: 'rgba(46, 204, 113, 0.4)'
        },
        loss: {
            bg: 'rgba(231, 76, 60, 0.15)',
            border: 'rgba(231, 76, 60, 0.3)',
            text: '#e74c3c',
            glow: 'rgba(231, 76, 60, 0.4)'
        },
        neutral: {
            bg: 'rgba(255, 255, 255, 0.08)',
            border: 'rgba(255, 255, 255, 0.15)',
            text: 'rgba(255,255,255,0.8)',
            glow: 'rgba(255, 255, 255, 0.2)'
        }
    };

    const sizeConfig = {
        small: { width: '26px', height: '26px', fontSize: '0.75rem' },
        medium: { width: '32px', height: '32px', fontSize: '0.875rem' }
    };

    const color = colors[variant];
    const sizeStyle = sizeConfig[size];

    return (
        <Box
            sx={{
                minWidth: sizeStyle.width,
                height: sizeStyle.height,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                fontWeight: 700,
                fontSize: sizeStyle.fontSize,
                backgroundColor: color.bg,
                color: color.text,
                border: `1px solid ${color.border}`,
                boxShadow: `inset 0 0 8px ${color.glow}`,
            }}
        >
            {score}
        </Box>
    );
};

// ============================================
// NEON RESULT BADGE
// ============================================

interface NeonResultBadgeProps {
    result: 'W' | 'L' | 'D';
}

export const NeonResultBadge: React.FC<NeonResultBadgeProps> = ({ result }) => {
    const colors = {
        W: { bg: 'rgba(46, 204, 113, 0.2)', text: '#2ecc71', border: 'rgba(46, 204, 113, 0.4)' },
        L: { bg: 'rgba(231, 76, 60, 0.2)', text: '#e74c3c', border: 'rgba(231, 76, 60, 0.4)' },
        D: { bg: 'rgba(241, 196, 15, 0.2)', text: '#f1c40f', border: 'rgba(241, 196, 15, 0.4)' }
    };

    const color = colors[result];

    return (
        <Box
            sx={{
                width: '28px',
                height: '22px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '4px',
                fontWeight: 700,
                fontSize: '0.65rem',
                backgroundColor: color.bg,
                color: color.text,
                border: `1px solid ${color.border}`,
                letterSpacing: '0.5px',
            }}
        >
            {result}
        </Box>
    );
};

// ============================================
// NEON LABEL BADGE (for league, tags, etc.)
// ============================================

interface NeonLabelBadgeProps {
    label: string;
    maxWidth?: string;
}

export const NeonLabelBadge: React.FC<NeonLabelBadgeProps> = ({ label, maxWidth = '100px' }) => {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    
    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                maxWidth,
                height: '20px',
                padding: '0 8px',
                borderRadius: '4px',
                backgroundColor: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.08)',
                overflow: 'hidden',
            }}
        >
            <Typography
                sx={{
                    fontSize: '0.6rem',
                    fontWeight: 600,
                    color: 'rgba(255,255,255,0.7)',
                    letterSpacing: '0.3px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {label}
            </Typography>
        </Box>
    );
};

// ============================================
// NEON EMPTY STATE
// ============================================

interface NeonEmptyStateProps {
    message?: string;
    height?: string;
}

export const NeonEmptyState: React.FC<NeonEmptyStateProps> = ({ 
    message = 'No data found', 
    height = '200px' 
}) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height,
            width: '100%',
        }}
    >
        <Typography
            sx={{
                color: 'rgba(255,255,255,0.25)',
                fontSize: '0.9rem',
                fontStyle: 'italic',
            }}
        >
            {message}
        </Typography>
    </Box>
);
